import React from 'react';
import { MainNavigationTab, UserProfile } from '../types';
import {
  MessageSquare,
  Sparkles,
  Compass,
  Shield,
  Phone,
  Settings,
  LayoutDashboard,
  FolderLock,
  Radio,
  Crown,
  Zap,
} from 'lucide-react';

interface WateenNavigationProps {
  activeTab: MainNavigationTab;
  onSelectTab: (tab: MainNavigationTab) => void;
  unreadMessagesCount: number;
  currentUser: UserProfile;
}

export const WateenNavigation: React.FC<WateenNavigationProps> = ({
  activeTab,
  onSelectTab,
  unreadMessagesCount,
  currentUser,
}) => {
  const navItems = [
    {
      id: 'hub' as MainNavigationTab,
      label: 'مركز وتين Hub',
      icon: Zap,
      badge: '🔥',
      isSpecialHub: true,
    },
    {
      id: 'chats' as MainNavigationTab,
      label: 'المحادثات',
      icon: MessageSquare,
      badge: unreadMessagesCount > 0 ? unreadMessagesCount : null,
    },
    {
      id: 'stories' as MainNavigationTab,
      label: 'اللحظات والقصص',
      icon: Sparkles,
      badge: null,
    },
    {
      id: 'discover' as MainNavigationTab,
      label: 'استكشف',
      icon: Compass,
      badge: null,
    },
    {
      id: 'vault' as MainNavigationTab,
      label: 'خزنة وتين',
      icon: FolderLock,
      badge: null,
    },
    {
      id: 'calls' as MainNavigationTab,
      label: 'المكالمات',
      icon: Phone,
      badge: null,
    },
    {
      id: 'premium' as MainNavigationTab,
      label: 'وتين Premium 👑',
      icon: Crown,
      badge: currentUser.isPremium ? 'VIP' : '🎁',
      isGolden: true,
    },
    {
      id: 'settings' as MainNavigationTab,
      label: 'الإعدادات',
      icon: Settings,
      badge: null,
    },
    {
      id: 'admin' as MainNavigationTab,
      label: 'لوحة الإدارة',
      icon: LayoutDashboard,
      badge: currentUser.isAdmin ? 'ADMIN' : 'إدارة 🛡️',
    },
  ];

  return (
    <nav className="fixed sm:static bottom-0 left-0 right-0 z-30 bg-[#080d16]/95 backdrop-blur-2xl border-t sm:border-t-0 sm:border-r border-slate-800/80 px-2 py-1.5 sm:py-4 flex sm:flex-col justify-start sm:justify-start gap-2 sm:w-20 lg:w-56 shrink-0 select-none font-['Cairo'] overflow-x-auto no-scrollbar scroll-smooth touch-pan-x">
      <div className="flex sm:flex-col gap-1 sm:gap-2 min-w-max sm:min-w-0 w-full px-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          const isGolden = (item as any).isGolden;
          const isSpecialHub = (item as any).isSpecialHub;

          return (
            <button
              key={item.id}
              onClick={() => onSelectTab(item.id)}
              className={`relative group flex flex-col sm:flex-row items-center gap-1 sm:gap-3 px-3 sm:px-3.5 py-1.5 sm:py-2.5 rounded-2xl transition-all duration-200 active:scale-90 min-w-[72px] sm:min-w-0 ${
                isActive
                  ? isGolden
                    ? 'bg-gradient-to-r from-amber-500/20 via-yellow-500/15 to-transparent text-amber-300 font-bold border border-amber-500/40 shadow-lg shadow-amber-500/10 scale-[1.02]'
                    : isSpecialHub
                    ? 'bg-gradient-to-r from-emerald-500/25 via-teal-500/20 to-indigo-500/10 text-emerald-300 font-bold border border-emerald-400/50 shadow-lg shadow-emerald-500/10 scale-[1.02]'
                    : 'bg-gradient-to-r from-emerald-500/15 to-teal-500/10 text-emerald-400 font-bold border border-emerald-500/30 scale-[1.02]'
                  : isGolden
                  ? 'text-amber-400/90 hover:text-amber-300 hover:bg-amber-500/10 hover:scale-105'
                  : isSpecialHub
                  ? 'text-emerald-400/90 hover:text-emerald-300 hover:bg-emerald-500/10 hover:scale-105'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.04] hover:scale-105'
              }`}
            >
              <div className="relative">
                <Icon
                  className={`w-5 h-5 transition-transform duration-200 group-hover:scale-110 ${
                    isActive
                      ? isGolden
                        ? 'text-amber-400 fill-amber-400/30 stroke-[2.4] scale-110'
                        : isSpecialHub
                        ? 'text-emerald-300 fill-emerald-400/20 stroke-[2.4] scale-110'
                        : 'text-emerald-400 stroke-[2.4] scale-110'
                    : isGolden
                    ? 'text-amber-400'
                    : isSpecialHub
                    ? 'text-emerald-400'
                    : 'stroke-[1.8]'
                }`}
              />
              {item.badge !== null && (
                <span
                  className={`absolute -top-1.5 -right-2 min-w-4 h-4 px-1 rounded-full text-[#080d16] text-[10px] font-black flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-110 ${
                    isGolden
                      ? 'bg-amber-400 text-slate-950 font-black ring-1 ring-amber-300'
                      : isSpecialHub
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black ring-1 ring-orange-300'
                      : 'bg-emerald-500 animate-pulse ring-1 ring-emerald-300'
                  }`}
                >
                  {item.badge}
                </span>
              )}
            </div>

            <span className="text-[10px] sm:hidden lg:inline lg:text-xs tracking-tight truncate transition-colors duration-200">
              {item.label}
            </span>

            {/* Indicator active bar for desktop */}
            {isActive && (
              <span
                className={`hidden sm:block absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-l-full ${
                  isGolden ? 'bg-amber-400' : 'bg-emerald-400'
                }`}
              />
            )}
          </button>
        );
      })}
      </div>

      {/* Developer attribution in desktop sidebar */}
      <div className="hidden lg:flex mt-auto pt-4 border-t border-slate-800/80 flex-col px-3 pb-2 text-right">
        <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span>تطبيق وتين (Wateen)</span>
        </div>
        <span className="text-[10px] text-slate-400 font-medium mt-1">تطوير وبرمجة:</span>
        <span className="text-[11px] font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-200">
          AYMEN QUSAY (أيمن قصي)
        </span>
      </div>
    </nav>
  );
};
